import { RefObject, useCallback } from 'react';
import { useKeyboardShortcut } from './useKeyboardShortcut';
import { isEditableElement } from '../../utils/shortcuts/isEditableElement';

const ARROW_KEY_DIRECTIONS: Record<string, { dx: -1 | 0 | 1; dy: -1 | 0 | 1 }> =
  {
    ArrowLeft: { dx: -1, dy: 0 },
    ArrowRight: { dx: 1, dy: 0 },
    ArrowUp: { dx: 0, dy: -1 },
    ArrowDown: { dx: 0, dy: 1 },
  };

type UseMoveLayoutObjectShortcutArgs = {
  // DOM root the shortcut dispatcher uses to decide the editor workspace owns the keydown.
  scopeRef: RefObject<HTMLElement | null>;
  selectedIndex: number | null;
  onMove: (dx: -1 | 0 | 1, dy: -1 | 0 | 1) => void;
};

// Moves the selected canvas object with the arrow keys; ignored while an
// editable element (e.g. a panel input) has focus.
export const useMoveLayoutObjectShortcut = ({
  scopeRef,
  selectedIndex,
  onMove,
}: UseMoveLayoutObjectShortcutArgs) => {
  const shouldHandle = useCallback(
    (event: KeyboardEvent) => {
      // The event may come from the PiP window — check the focus of the
      // document that fired it, not the main one.
      const eventDocument =
        (event.target as Node | null)?.ownerDocument ?? document;

      return (
        event.key in ARROW_KEY_DIRECTIONS &&
        selectedIndex !== null &&
        !isEditableElement(eventDocument.activeElement)
      );
    },
    [selectedIndex]
  );

  const onTrigger = useCallback(
    (event: KeyboardEvent) => {
      const direction = ARROW_KEY_DIRECTIONS[event.key];

      if (!direction) {
        return;
      }

      // Arrows scroll the canvas viewport otherwise.
      event.preventDefault();
      onMove(direction.dx, direction.dy);
    },
    [onMove]
  );

  useKeyboardShortcut({
    id: 'layout-editor-move-object',
    scopeRef,
    shouldHandle,
    onTrigger,
  });
};
